import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Edit2, ArrowUpRight, Trash2, Eye, Download } from "lucide-react";
import { DataTable, Avatar, TableActionButton } from "@/components/ui";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import { MANAGE_STAGES_TEXT } from "@/constants/globalConstants";
import { StageDropdown } from "../../leads/components/StageDropdown";

export const InitialQuotationTableView = ({
  leads,
  isLoading,
  onLeadClick,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onDownloadClick, // Add this prop
}) => {
  const getApprovalStatus = (approval) => {
    if (!approval || !approval.isRequested)
      return { text: "Not Requested", color: "text-slate-400 bg-slate-100" };

    const { designer, manager, owner } = approval;
    if (owner?.status === "approved")
      return { text: "Approved", color: "text-green-700 bg-green-100" };

    if (owner?.status === "rejected")
      return { text: "Rejected by Owner", color: "text-red-700 bg-red-100" };
    if (manager?.status === "rejected")
      return { text: "Rejected by Manager", color: "text-red-700 bg-red-100" };
    if (designer?.status === "rejected")
      return { text: "Rejected by Designer", color: "text-red-700 bg-red-100" };

    return { text: "Requested", color: "text-blue-700 bg-blue-100" };
  };
  const columns = useMemo(() => {
    const cols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: "170px",
        render: (l) => (
          <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
            {l.customId || "—"}
          </span>
        ),
      },
      {
        header: "Contact Details",
        key: "customerName",
        render: (l) => {
          const fullName =
            l.customerName ||
            `${l.firstName || ""} ${l.lastName || ""}`.trim() ||
            "No Name";
          return (
            <div className="flex items-center gap-2.5">
              <Avatar name={fullName} size="xs" />
              <div className="flex flex-col min-w-0">
                <span className="text-brand-dark tracking-tight text-sm leading-tight truncate">
                  {fullName}
                </span>
                <span className="text-[10px] text-slate-400 truncate flex items-center gap-1 mt-0.5  tracking-tighter">
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="mx-1 opacity-50">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        header: "Last Note",
        key: "lastNote",
        className: "min-w-[250px]",
        render: (l) => {
          // As per request, keep as '-' for now
          let displayNote =
            l.stageNotes?.initial_quotation || l.quotationNote || "—";
          if (displayNote && typeof displayNote === "string") {
            if (displayNote.includes(" | Note: ")) {
              displayNote = displayNote.split(" | Note: ")[1].trim();
            } else if (
              displayNote.startsWith("Lead Status changed") ||
              displayNote.startsWith("Design Status changed") ||
              displayNote.startsWith("Appointment scheduled") ||
              displayNote.startsWith("Lead assigned") ||
              displayNote.startsWith("Lead reassigned")
            ) {
              displayNote = "—";
            }
          }
          return (
            <span className="text-[11px] text-slate-600 italic block leading-normal py-1">
              {displayNote || "—"}
            </span>
          );
        },
      },
      {
        header: "Estimated Amount",
        key: "quotationAmount",
        render: (l) => (
          <span className="text-[11px] font-bold text-slate-700">
            {l.quotationAmount
              ? `₹${l.quotationAmount.toLocaleString("en-IN")}`
              : "—"}
          </span>
        ),
      },
      {
        header: "Approval Status",
        key: "approvalStatus",
        render: (l) => {
          const status = getApprovalStatus(l.initialQuotationApproval);
          return (
            <span
              className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider ${status.color}`}
            >
              {status.text}
            </span>
          );
        },
      },
      {
        header: "Actions",
        key: "actions",
        render: (l) => {
          const approval = l.initialQuotationApproval || {};
          const isRequested = approval.isRequested;
          const isRejected =
            approval.designer?.status === "rejected" ||
            approval.manager?.status === "rejected" ||
            approval.owner?.status === "rejected";
          const isFullyApproved = approval.owner?.status === "approved";

          // Edit and Delete are disabled if requested AND not rejected
          const disableEditDelete = isRequested && !isRejected;

          return (
            <div className="flex items-center gap-2 justify-end">
              <TableActionButton
                icon={Eye}
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewClick && onViewClick(l);
                }}
                title="View Details"
              />
            </div>
          );
        },
      },
    ];

    return cols;
  }, [onLeadClick, onEditClick, onDeleteClick, onViewClick]);
  return (
    <DataTable
      columns={columns}
      data={leads}
      isLoading={isLoading}
      getRowClassName={(idx, row) =>
        row?.isDisabled ? "opacity-50 bg-slate-50/50" : ""
      }
      className="border-none shadow-none rounded-none"
    />
  );
};
